<template>
  <div class="new-dispatch-modal">
    <div class="form-grid">
      <div class="form-group">
        <label for="datePicker">Dispatch Date<span class="required">*</span></label>
        <input
            type="date"
            v-model="formData.date"
            id="datePicker"
            class="search-input styled-input"
            disabled
        >
      </div>

      <div class="form-group">
        <label for="salesRep">Vehicle <span class="required">*</span></label>
        <select v-model="formData.salesRep" id="salesRep" class="styled-input" :disabled="hasSelectedAllocations">
          <option disabled value="">Select Vehicle</option>
          <option v-for="rep in salesReps" :key="rep.profile_id" :value="rep.profile_id">{{ rep.name }}</option>
        </select>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="formData.salesRep && allocationsLoading" class="allocations-loading">
      <i class="fa fa-spinner fa-spin"></i>
      <span>Fetching stock allocations…</span>
    </div>

    <!-- Allocations list -->
    <div v-else-if="formData.salesRep && selectedRepStockAllocations.length" class="allocations-card">
      <div class="allocations-card__header">
        <span class="allocations-eyebrow">Stock Allocations</span>
        <span class="allocations-count">{{ selectedRepStockAllocations.length }} found</span>
      </div>

      <div class="allocations-table-wrap">
        <table class="allocations-table">
          <thead>
          <tr>
            <th class="col-select"></th>
            <th>Allocation No</th>
            <th>Date</th>
            <th>Item</th>
            <th>Qty</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="allocation in selectedRepStockAllocations"
              :key="allocation.id"
              :class="{ 'is-selected': selectedAllocations.includes(allocation.id) }"
          >
            <td class="col-select">
              <input type="checkbox" v-model="selectedAllocations" :value="allocation.id">
            </td>
            <td class="allocation-no">{{ allocation.stock_allocations_no }}</td>
            <td>{{ allocation.stock_allocations_date }}</td>
            <td>
              <ul class="item-list">
                <li v-for="item in allocation.items" :key="item.item_id">{{ item.item_name }}</li>
              </ul>
            </td>
            <td>
              <ul class="item-list">
                <li v-for="item in allocation.items" :key="item.item_id">{{ parseInt(item.item_qty) }}</li>
              </ul>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="formData.salesRep && !selectedRepStockAllocations.length" class="allocations-empty">
      <i class="fa fa-inbox"></i>
      <p>No stock allocations found</p>
      <span>This vehicle has no pending stock allocations to dispatch.</span>
    </div>

    <div class="button-group mt-5">
      <button type="submit" class="btn btn-success" @click="generateDispatch" :disabled="submitLoading">
        <i v-if="submitLoading" class="fa fa-spinner fa-spin mr-2"></i>
        {{ submitLoading ? 'Generating...' : 'Generate Dispatch' }}
      </button>
    </div>

    <!-- Conflict Modal -->
    <GatePassConflictModal
        v-if="showConflictModal"
        :message="conflictMessage"
        :dispatches="conflictData"
        @close="showConflictModal = false"
    />

    <!-- ======================== FLOATING ACTION BUTTON (hidden when panel open) ======================== -->
    <transition name="ds-fab-fade">
      <button
          v-if="!creditPanel.visible"
          class="ds-fab"
          @click="openCreditPanel"
          title="View Credit Exceeded Dealers"
      >
        <i class="fa fa-users"></i>
      </button>
    </transition>

    <!-- ======================== CREDIT EXCEEDED DEALERS PANEL ======================== -->
    <transition name="ds-slide">
      <div
          v-if="creditPanel.visible"
          class="ds-panel"
          :class="{ 'ds-panel--minimized': creditPanel.minimized }"
      >
        <!-- Header -->
        <div class="ds-panel__header" @click="creditPanel.minimized && toggleMinimize()">
          <div class="ds-panel__header-top">
            <div class="ds-panel__title">
              <i class="fa fa-exclamation-triangle" style="color: #f59e0b;"></i>
              <span>Credit Exceeded Dealers</span>
            </div>
            <div class="ds-panel__actions">
              <!-- Custom Sort Menu -->
              <div class="ds-sort-wrapper" v-show="!creditPanel.minimized && !creditDealersLoading && creditExceededDealers.length > 0" title="Sort Options">
                <button class="ds-icon-btn" @click.stop="showSortMenu = !showSortMenu">
                  <i class="fa fa-sort"></i>
                </button>
                <transition name="ds-fade">
                  <div v-if="showSortMenu" class="ds-sort-menu">
                    <div class="ds-sort-menu-item" :class="{ active: sortOrder === 'default' }" @click.stop="setSortOrder('default')">Default Order</div>
                    <div class="ds-sort-menu-item" :class="{ active: sortOrder === 'limit-asc' }" @click.stop="setSortOrder('limit-asc')">Credit Limit (Min - Max)</div>
                    <div class="ds-sort-menu-item" :class="{ active: sortOrder === 'limit-desc' }" @click.stop="setSortOrder('limit-desc')">Credit Limit (Max - Min)</div>
                    <div class="ds-sort-menu-item" :class="{ active: sortOrder === 'exceeded-asc' }" @click.stop="setSortOrder('exceeded-asc')">Exceeded Value (Min - Max)</div>
                    <div class="ds-sort-menu-item" :class="{ active: sortOrder === 'exceeded-desc' }" @click.stop="setSortOrder('exceeded-desc')">Exceeded Value (Max - Min)</div>
                  </div>
                </transition>
              </div>
              <button class="ds-icon-btn" @click.stop="toggleMinimize" :title="creditPanel.minimized ? 'Expand' : 'Minimize'">
                <i :class="creditPanel.minimized ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
              </button>
              <button class="ds-icon-btn ds-icon-btn--close" @click.stop="closeCreditPanel" title="Close">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
          
          <div class="ds-panel__header-meta" v-show="!creditPanel.minimized" style="justify-content: space-between; align-items: center; padding-top: 5px;">
            <span style="font-size: 0.8rem; opacity: 0.9;">Total: {{ creditExceededDealers.length }} dealers</span>
            <!-- Toggle switch for scheduled dealers -->
            <label class="toggle-switch">
              <input type="checkbox" v-model="filterScheduledOnly" @click.stop>
              <span class="toggle-slider"></span>
              <span class="toggle-label">Scheduled Only</span>
            </label>
          </div>
        </div>

        <!-- Body -->
        <div class="ds-panel__body" v-show="!creditPanel.minimized">
          <div v-if="creditDealersLoading" class="ds-empty">
            <i class="fa fa-spinner fa-spin" style="color: #2563eb;"></i>
            <p>Loading...</p>
          </div>
          <div v-else-if="creditExceededDealers.length === 0" class="ds-empty">
            <i class="fa fa-check-circle" style="color: #10b981;"></i>
            <p>No dealers found.</p>
          </div>
          
          <div v-else class="ds-list">
            <div v-for="dealer in sortedCreditExceededDealers" :key="dealer.dealer_id" class="ds-schedule-card">
              <div class="ds-schedule-body">
                <div class="ds-route-name" style="border: none; padding-bottom: 0; margin-bottom: 4px;">
                  <span><i class="fa fa-user" style="color: #6b7280; margin-right: 5px;"></i> {{ dealer.business_name }}</span>
                </div>
                <div class="ds-dealer-address" style="margin-bottom: 4px;">
                  <i class="fa fa-map-marker" style="color: #9ca3af; width: 14px; text-align: center;"></i> {{ dealer.address || 'N/A' }}
                </div>
                <div class="ds-dealer-address" style="margin-bottom: 8px;">
                  <i class="fa fa-phone" style="color: #9ca3af; width: 14px; text-align: center;"></i> {{ dealer.mobile || 'N/A' }}
                </div>
                <div style="display: flex; gap: 10px; font-size: 0.75rem; flex-wrap: wrap;">
                  <div style="background: #f3f4f6; color: #374151; padding: 4px 8px; border-radius: 4px; font-weight: 600;">
                    Limit: Rs {{ Number(dealer.credit_limit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  </div>
                  <div style="background: #fee2e2; color: #b91c1c; padding: 4px 8px; border-radius: 4px; font-weight: 600;">
                    Exceeded: Rs {{ Number(dealer.exceeded_credit_limit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { fetchSalesReps, fetchREpDispatchList, saveDispatch, fetchCreditExceededDealers } from '@/services/dataService';
import GatePassConflictModal from '@/pages/Modal/GatePassConflictModal.vue';
import { useRouter, useRoute } from "vue-router";
import { useToast } from 'vue-toastification';

export default {
  name: "NewDispatch",
  components: {
    GatePassConflictModal
  },
  data() {
    return {
      formData: {
        date: new Date().toISOString().split('T')[0],
        salesRep: "",
        company_id: null
      },
      salesReps: [],
      selectedRepStockAllocations: [],
      selectedAllocations: [],
      allocationsLoading: false,
      profile_id: null,
      reporting_profile_id: null,
      user_category: null,
      token: localStorage.getItem('token_set') || '',
      submitLoading: false,
      showConflictModal: false,
      conflictMessage: '',
      conflictData: [],
      
      creditPanel: {
        visible: false,
        minimized: false,
      },
      filterScheduledOnly: false,
      sortOrder: 'default',
      showSortMenu: false,
      creditDealersLoading: false,
      creditExceededDealers: []
    };
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const toast = useToast();
    return { router, route, toast };
  },
  computed: {
    hasSelectedAllocations() {
      return this.selectedAllocations.length > 0;
    },
    sortedCreditExceededDealers() {
      let dealers = [...this.creditExceededDealers];
      if (this.sortOrder === 'limit-asc') {
        dealers.sort((a, b) => Number(a.credit_limit || 0) - Number(b.credit_limit || 0));
      } else if (this.sortOrder === 'limit-desc') {
        dealers.sort((a, b) => Number(b.credit_limit || 0) - Number(a.credit_limit || 0));
      } else if (this.sortOrder === 'exceeded-asc') {
        dealers.sort((a, b) => Number(a.exceeded_credit_limit || 0) - Number(b.exceeded_credit_limit || 0));
      } else if (this.sortOrder === 'exceeded-desc') {
        dealers.sort((a, b) => Number(b.exceeded_credit_limit || 0) - Number(a.exceeded_credit_limit || 0));
      }
      return dealers;
    }
  },
  async mounted() {
    this.profile_id = localStorage.getItem('profile_id');
    this.reporting_profile_id = localStorage.getItem('reportingL1ProfileId');
    this.user_category = localStorage.getItem('user_label');
    this.formData.company_id = localStorage.getItem('company_id');
    await this.fetchDispatchData();

    const preselectedVehicle = this.route?.query?.vehicle;
    if (preselectedVehicle) {
      this.formData.salesRep = Array.isArray(preselectedVehicle)
          ? preselectedVehicle[0]
          : preselectedVehicle;
    }
  },
  watch: {
    'formData.salesRep': async function (newVal) {
      this.selectedRepStockAllocations = [];
      this.selectedAllocations = [];
      if (newVal) await this.fetchRepStockAllocations(newVal);
    },
    filterScheduledOnly: function() {
      this.fetchCreditDealers();
    }
  },
  methods: {
    async fetchDispatchData() {
    const reportigProfId = this.user_category === 'DISTRIBUTOR BACK OFFICE' ? this.reporting_profile_id : this.profile_id;
        
      try {
        const reps = await fetchSalesReps(this.profile_id, reportigProfId, this.token);
        this.salesReps = reps?.data?.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    async fetchRepStockAllocations(profileId) {
      this.allocationsLoading = true;
      try {
        const res = await fetchREpDispatchList(profileId, this.token);
        this.selectedRepStockAllocations = res.data.data ?? [];
      } catch (e) {
        console.error(e);
        this.selectedRepStockAllocations = [];
      } finally {
        this.allocationsLoading = false;
      }
    },
    getValidationError() {
      if (!this.formData.salesRep) return "Please select a Vehicle.";
      if (this.selectedAllocations.length === 0) return "Please select at least one Stock Allocation.";
      return null;
    },
    async generateDispatch() {
      const errorMsg = this.getValidationError();
      if (errorMsg) {
        this.toast.error(errorMsg);
        return;
      }

      try {
        this.submitLoading = true;
        const activeProfileId = this.user_category === 'DISTRIBUTOR BACK OFFICE' ? this.reporting_profile_id : this.profile_id;
        const payload = {
          created_by: activeProfileId,
          company_id: this.formData.company_id,
          checked_by: activeProfileId,
          rep_id: this.formData.salesRep,
          stock_allocations_id: this.selectedAllocations,
          sales_dispatches_date: this.formData.date
        };

        const response = await saveDispatch(payload, this.token);
        if (response.data.status === "success") {
          this.toast.success(response.data.message);
          this.router.push('/dispatch');
        } else if (response.data.status === "conflict") {
          this.conflictMessage = response.data.message || "Before creating a new dispatch, ensure that the gate pass is in for dispatches from more than 2 days ago.";
          this.conflictData = response.data.data || [];
          this.showConflictModal = true;
        } else {
          this.toast.error(response.data.message);
        }
      } catch (error) {
        this.toast.error("Failed to generate dispatch.");
      } finally {
        this.submitLoading = false;
      }
    },
    openCreditPanel() {
      this.creditPanel.visible = true;
      this.creditPanel.minimized = false;
      this.fetchCreditDealers();
    },
    closeCreditPanel() {
      this.creditPanel.visible = false;
      this.showSortMenu = false;
    },
    toggleMinimize() {
      this.creditPanel.minimized = !this.creditPanel.minimized;
      this.showSortMenu = false;
    },
    setSortOrder(order) {
      this.sortOrder = order;
      this.showSortMenu = false;
    },
    async fetchCreditDealers() {
      this.creditDealersLoading = true;
      try {
        const distributorProfileId = this.user_category === 'DISTRIBUTOR BACK OFFICE' ? this.reporting_profile_id : this.profile_id;
        const res = await fetchCreditExceededDealers(distributorProfileId, this.filterScheduledOnly, this.token);
        if (res.data.status === 'success') {
          this.creditExceededDealers = res.data.data || [];
        } else {
          this.creditExceededDealers = [];
        }
      } catch (error) {
        console.error("Failed to fetch credit exceeded dealers", error);
        this.creditExceededDealers = [];
      } finally {
        this.creditDealersLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.new-dispatch-modal {
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

@media (max-width: 600px) { .form-grid { grid-template-columns: 1fr; } }

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
}

label {
  font-weight: bold;
  margin-bottom: 8px;
  color: #1b2a50;
}

.required { color: red; }

.styled-input {
  height: 42px;
  padding: 0 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  width: 100%;
}

.styled-input:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  border-style: dashed;
}

/* ======================== LOADING STATE ======================== */
.allocations-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  margin-top: 20px;
  color: #2563eb;
  font-size: 0.9rem;
  font-weight: 500;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.allocations-loading .fa {
  font-size: 2rem;
}

/* ======================== EMPTY STATE ======================== */
.allocations-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  margin-top: 20px;
  text-align: center;
  color: #9ca3af;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px dashed #e5e7eb;
}

.allocations-empty .fa {
  font-size: 2.2rem;
  color: #d1d5db;
  margin-bottom: 4px;
}

.allocations-empty p {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #6b7280;
}

.allocations-empty span {
  font-size: 0.8rem;
  color: #9ca3af;
}

/* ======================== ALLOCATIONS CARD ======================== */
.allocations-card {
  margin-top: 20px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.allocations-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f8faff;
  border-bottom: 1px solid #e5e7eb;
}

.allocations-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}

.allocations-count {
  font-size: 0.72rem;
  font-weight: 600;
  color: #2563eb;
  background: #dbeafe;
  border-radius: 20px;
  padding: 3px 10px;
}

.allocations-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 320px;
}

.allocations-table {
  width: 100%;
  border-collapse: collapse;
}

.allocations-table th {
  padding: 10px 14px;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  /* Sticky header */
  position: sticky;
  top: 0;
  background: #f8faff;
  z-index: 1;
}

.allocations-table td {
  padding: 12px 14px;
  font-size: 0.85rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.allocations-table tbody tr:last-child td {
  border-bottom: none;
}

.allocations-table tbody tr:hover {
  background: #f9fafb;
}

.allocations-table tbody tr.is-selected {
  background: #eff6ff;
}

.allocations-table .col-select {
  width: 40px;
}

.allocation-no {
  font-weight: 600;
  color: #1f2937;
}

.item-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.table {
  width: 100%;
  margin-top: 20px;
  border-collapse: collapse;
}

.table th, .table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.btn-success {
  background-color: #28a745;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.button-group {
  display: flex;
  justify-content: flex-end;
}

/* ======================== FLOATING ACTION BUTTON ======================== */
.ds-fab {
  position: fixed;
  right: 28px;
  bottom: 28px;
  z-index: 1200;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s;
}

.ds-fab:hover {
  transform: translateY(-3px) scale(1.08);
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.5);
  background: #1d4ed8;
}

.ds-fab:active {
  transform: scale(0.94);
}

.ds-fab-fade-enter-active, .ds-fab-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.ds-fab-fade-enter-from, .ds-fab-fade-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

/* ======================== PANEL ======================== */
.ds-panel {
  position: fixed;
  right: 24px;
  bottom: 48px;
  z-index: 1100;
  width: 420px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s, bottom 0.25s ease, border-radius 0.25s ease;
}

.ds-panel--minimized {
  bottom: 0 !important;
  right: 24px !important;
  border-radius: 10px 10px 0 0;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.12);
  width: 320px;
}

.ds-panel--minimized .ds-panel__header {
  border-radius: 10px 10px 0 0;
  cursor: pointer;
}

.ds-panel__header {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #fff;
  user-select: none;
  border-radius: 14px 14px 0 0;
  flex-shrink: 0;
  cursor: default;
}

.ds-panel__header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 10px;
}

.ds-panel__header-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 14px 10px;
}

.ds-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 0.88rem;
  letter-spacing: 0.01em;
  color: #fff;
}

.ds-panel__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ds-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: rgba(255,255,255,0.15);
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.ds-icon-btn:hover { background: rgba(255,255,255,0.28); }
.ds-icon-btn--close:hover { background: rgba(239,68,68,0.7); }

.ds-panel__body {
  flex: 1;
  overflow-y: auto;
  max-height: 420px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 0 0 14px 14px;
}

.ds-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 16px;
  color: #9ca3af;
  font-size: 0.9rem;
  text-align: center;
}
.ds-empty .fa { font-size: 2rem; color: #d1d5db; }
.ds-empty p { margin: 0; }

.ds-schedule-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.ds-schedule-body {
  padding: 12px 14px;
}

.ds-route-name {
  font-weight: 600;
  font-size: 0.82rem;
  color: #1D2128;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ds-dealer-address {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ds-slide-enter-active, .ds-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.ds-slide-enter-from, .ds-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.97);
}

/* Toggle Switch Styles */
.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.toggle-switch input {
  display: none;
}

.toggle-slider {
  position: relative;
  width: 34px;
  height: 18px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 34px;
  transition: .4s;
  margin-right: 8px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  border-radius: 50%;
  transition: .4s;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #10b981;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(16px);
}

.toggle-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
}

.ds-sort-wrapper {
  position: relative;
  display: inline-block;
}

.ds-sort-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  width: 230px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  padding: 6px;
  border: 1px solid #e5e7eb;
}

.ds-sort-menu-item {
  padding: 10px 12px;
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
}

.ds-sort-menu-item:hover {
  background: #f3f4f6;
}

.ds-sort-menu-item.active {
  background: #eff6ff;
  color: #2563eb;
  font-weight: 600;
}

.ds-fade-enter-active, .ds-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.ds-fade-enter-from, .ds-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>